/**
 * The Change tab — Step 49.
 *
 * Kind, the three gated plans, the risk matrix and the planned window.
 *
 * The risk is PREVIEWED here from the same `deriveChangeRisk` the server
 * stores with — one function, two runtimes, which is the reason
 * `@itsm/shared` exists. Somebody choosing C1 should see "critical" appear
 * before they save, not discover it afterwards.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { RichTextEditor } from '../../components/ui/richtext';
import { apiFetch, apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { useDirty } from '../../lib/dirty';
import { cn } from '../../lib/utils';
import {
  CHANGE_IMPACTS,
  CHANGE_IMPACT_LABEL,
  CHANGE_KINDS,
  CHANGE_KIND_HELP,
  CHANGE_KIND_LABEL,
  CHANGE_LIKELIHOODS,
  CHANGE_LIKELIHOOD_LABEL,
  deriveChangeRisk,
  type ChangeImpact,
  type ChangeKind,
  type ChangeLikelihood,
} from '@itsm/shared';

interface ChangePlan {
  kind: string;
  reviewOutcome: string | null;
  reviewNote: string | null;
  justification: string | null;
  backoutPlan: string | null;
  testPlan: string | null;
  implementationPlan: string | null;
  impact: string | null;
  likelihood: string | null;
  risk: string | null;
}

/** Every value the form owns, in one shape — the baseline, the payload and
 *  the dirty check all read it, so none of them can drift from the others. */
interface ChangeForm {
  kind: ChangeKind;
  text: Record<string, string>;
  impact: string;
  outcome: string;
  likelihood: string;
  start: string;
  end: string;
}

const RISK_CLASS: Record<string, string> = {
  low: 'border-emerald-500/40 text-emerald-700 dark:text-emerald-400',
  medium: 'border-amber-500/40 text-amber-700 dark:text-amber-400',
  high: 'border-orange-500/50 text-orange-700 dark:text-orange-400',
  critical: 'border-destructive/50 text-destructive',
};

/** The three GATED plans, plus the one that is not. Kept in this order
 *  because it is the order the gate names them in. */
const PLANS = [
  { key: 'justification' as const, label: 'Justification', gated: true, hint: 'Why it is worth the risk.' },
  {
    key: 'implementationPlan' as const,
    label: 'Implementation plan',
    gated: false,
    hint: 'What will be done.',
  },
  {
    key: 'testPlan' as const,
    label: 'Test plan',
    gated: true,
    hint: 'How you will know it worked.',
  },
  {
    key: 'backoutPlan' as const,
    label: 'Backout plan',
    gated: true,
    hint: 'How to undo it.',
  },
];

/** epoch ms ⇄ the value a `datetime-local` input wants. */
const toLocal = (ms: number | null | undefined) => {
  if (!ms) return '';
  const d = new Date(ms - new Date(ms).getTimezoneOffset() * 60_000);
  return d.toISOString().slice(0, 16);
};

export function ChangeTab({
  id,
  plannedStartAt,
  plannedEndAt,
  canEdit,
}: {
  id: string;
  plannedStartAt: number | null;
  plannedEndAt: number | null;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: qk.tickets.change(id),
    queryFn: () => apiFetch<{ entry: ChangePlan | null }>(`/api/tickets/${id}/change`),
  });

  const [kind, setKind] = useState<ChangeKind>('normal');
  const [text, setText] = useState<Record<string, string>>({});
  const [impact, setImpact] = useState('');
  const [likelihood, setLikelihood] = useState('');
  const [outcome, setOutcome] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  /**
   * What the server last agreed to — and the seeded-yet latch, because they
   * are the same fact.
   *
   * Step 192. `dirty` used to compare the form against the live query cache,
   * and onSuccess re-armed a `loaded` boolean to re-seed from it.
   * `invalidateQueries` does not refetch synchronously, so that re-seed ran a
   * render later against the STILL-CACHED PRE-SAVE row: on a change that had
   * no plan yet, every field blanked the moment you pressed Save. Pressing
   * Save again then wrote those blanks over the plan that had just been
   * stored, and the button never went quiet because the form and the cache
   * were never looking at the same version.
   *
   * A baseline cannot race. It moves when we seed and when a save lands, and
   * at no other time.
   */
  const [base, setBase] = useState<ChangeForm | null>(null);

  useEffect(() => {
    if (!q.data || base) return;
    const e = q.data.entry;
    const initial: ChangeForm = {
      kind: (e?.kind as ChangeKind) ?? 'normal',
      text: {
        justification: e?.justification ?? '',
        implementationPlan: e?.implementationPlan ?? '',
        testPlan: e?.testPlan ?? '',
        backoutPlan: e?.backoutPlan ?? '',
      },
      impact: e?.impact ?? '',
      outcome: e?.reviewOutcome ?? '',
      likelihood: e?.likelihood ?? '',
      start: toLocal(plannedStartAt),
      end: toLocal(plannedEndAt),
    };
    setKind(initial.kind);
    setText(initial.text);
    setImpact(initial.impact);
    setOutcome(initial.outcome);
    setLikelihood(initial.likelihood);
    setStart(initial.start);
    setEnd(initial.end);
    setBase(initial);
  }, [q.data, base, plannedStartAt, plannedEndAt]);

  /** The form as it stands, in one value — so what we send, what we compare
   *  against and what becomes the new baseline cannot drift apart. */
  const form: ChangeForm = { kind, text, impact, outcome, likelihood, start, end };
  const dirty =
    !!base &&
    (form.kind !== base.kind ||
      PLANS.some((p) => (form.text[p.key] ?? '') !== (base.text[p.key] ?? '')) ||
      form.impact !== base.impact ||
      form.outcome !== base.outcome ||
      form.likelihood !== base.likelihood ||
      form.start !== base.start ||
      form.end !== base.end);
  const clearDirty = useDirty(dirty);

  // Previewed with the SAME function the server stores with.
  const preview =
    impact && likelihood ? deriveChangeRisk(impact as ChangeImpact, likelihood as ChangeLikelihood) : null;

  const save = useMutation({
    mutationFn: () =>
      apiSend(`/api/tickets/${id}/change`, 'PUT', {
        kind: form.kind,
        ...form.text,
        impact: form.impact || null,
        reviewOutcome: form.outcome || null,
        likelihood: form.likelihood || null,
        plannedStartAt: form.start ? new Date(form.start).getTime() : null,
        plannedEndAt: form.end ? new Date(form.end).getTime() : null,
      }),
    onSuccess: (r) => {
      if (!r.ok) return toast.error(r.error ?? 'Could not save.');
      // The form we SENT is the new baseline — not the form as it stands now,
      // which may have moved on while the request was in flight.
      setBase(form);
      clearDirty();
      toast.success('Saved.');
      void qc.invalidateQueries({ queryKey: qk.tickets.change(id) });
      void qc.invalidateQueries({ queryKey: qk.tickets.detail(id) });
    },
  });

  if (q.isPending)
    return (
      <div className="space-y-3 p-4">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-semibold">Kind</label>
            <Select value={kind} disabled={!canEdit} onChange={(e) => setKind(e.target.value as ChangeKind)}>
              {CHANGE_KINDS.map((k) => (
                <option key={k} value={k}>
                  {CHANGE_KIND_LABEL[k]}
                </option>
              ))}
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">{CHANGE_KIND_HELP[kind]}</p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold">Risk</label>
            <div className="flex items-center gap-2">
              <Select
                value={impact}
                disabled={!canEdit}
                onChange={(e) => setImpact(e.target.value)}
                aria-label="Change impact"
              >
                <option value="">Impact…</option>
                {CHANGE_IMPACTS.map((i) => (
                  <option key={i} value={i}>
                    {CHANGE_IMPACT_LABEL[i]}
                  </option>
                ))}
              </Select>
              <Select
                value={likelihood}
                disabled={!canEdit}
                onChange={(e) => setLikelihood(e.target.value)}
                aria-label="Likelihood"
              >
                <option value="">Likelihood…</option>
                {CHANGE_LIKELIHOODS.map((l) => (
                  <option key={l} value={l}>
                    {CHANGE_LIKELIHOOD_LABEL[l]}
                  </option>
                ))}
              </Select>
            </div>

            {preview && (
              <p
                className={cn(
                  'mt-2 inline-block rounded-md border px-2 py-1 text-xs font-semibold uppercase tracking-wide',
                  RISK_CLASS[preview],
                )}
              >
                {preview} risk
              </p>
            )}
          </div>
        </div>
        {/* The OUTCOME moved to the properties rail — Step 198. It answers
            "what happened", after the window; everything on this tab answers
            "what we intend", before it. Two questions, two moments, and the
            second was being skipped at the bottom of the first. */}

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-semibold">Planned start</label>
            {/* `dark:[color-scheme:dark]` — the standing rule for native date
              inputs. */}
            <Input
              type="datetime-local"
              value={start}
              disabled={!canEdit}
              onChange={(e) => setStart(e.target.value)}
              className="dark:[color-scheme:dark]"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold">Planned end</label>
            <Input
              type="datetime-local"
              value={end}
              disabled={!canEdit}
              onChange={(e) => setEnd(e.target.value)}
              className="dark:[color-scheme:dark]"
            />
          </div>
        </div>

        {/* Step 143 — planned inside a freeze window? Named, with the owner and
            the reason, so the conversation that needs to happen can happen. A
            warning, not a block: the rule "Flag a change planned inside a
            freeze window" decides what else follows. */}
        <FreezeWarning start={start} end={end} kind={kind} />

        {PLANS.map((p) => (
          <div key={p.key}>
            <label className="mb-1 flex items-center gap-2 text-sm font-semibold">
              {p.label}
              {/* Which ones the gate actually checks, said on the field rather
                than discovered when the stepper refuses to move. */}
              {p.gated && (
                <span className="rounded bg-muted px-1.5 text-[11px] font-normal text-muted-foreground">
                  required
                </span>
              )}
            </label>
            <p className="mb-1.5 text-xs text-muted-foreground">{p.hint}</p>
            {canEdit ? (
              <RichTextEditor
                value={text[p.key] ?? ''}
                onChange={(html) => setText((t) => ({ ...t, [p.key]: html }))}
              />
            ) : (
              <div
                className="prose prose-sm dark:prose-invert max-w-none overflow-x-auto rounded-md border p-3"
                dangerouslySetInnerHTML={{ __html: text[p.key] || '<p>—</p>' }}
              />
            )}
          </div>
        ))}
      </div>
      {/* Docked to the bottom of the viewport, like the properties rail's
          Update (Kai). The body above scrolls; this does not, so on a long
          write-up Save is never a scroll away. */}
      {canEdit && (
        <div className="flex shrink-0 items-center gap-2 border-t bg-card px-4 py-2.5">
          <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
          {dirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
        </div>
      )}
    </div>
  );
}

function FreezeWarning({ start, end, kind }: { start: string; end: string; kind: string }) {
  const from = start ? new Date(start).getTime() : NaN;
  const to = end ? new Date(end).getTime() : from;
  const ok = Number.isFinite(from) && Number.isFinite(to);
  const q = useQuery({
    queryKey: qk.freezeWindowsRange(ok ? from : 0, ok ? to : 0),
    queryFn: () =>
      apiFetch<{
        entries: {
          id: string;
          name: string;
          reason: string | null;
          startsAt: number;
          endsAt: number;
          ownerName: string | null;
          allowEmergency: boolean;
        }[];
      }>(`/api/tickets/freeze-windows?from=${from}&to=${Math.max(to, from + 1)}`),
    enabled: ok,
  });
  const hits = (q.data?.entries ?? []).filter((f) => !(f.allowEmergency && kind === 'emergency'));
  if (!ok || hits.length === 0) return null;
  const when = (ms: number) =>
    new Date(ms).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  return (
    <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-900 dark:text-amber-200">
      <p className="font-medium">
        Planned inside {hits.length === 1 ? 'a freeze window' : `${hits.length} freeze windows`}
      </p>
      <ul className="mt-1 space-y-1 text-xs">
        {hits.map((f) => (
          <li key={f.id}>
            <span className="font-medium">{f.name}</span> · {when(f.startsAt)} → {when(f.endsAt)}
            {f.ownerName ? ` · owned by ${f.ownerName}` : ''}
            {f.reason ? ` — ${f.reason}` : ''}
            {!f.allowEmergency && ' · blocks emergency changes too'}
          </li>
        ))}
      </ul>
      <p className="mt-1 text-xs">Speak to the window's owner or move the change.</p>
    </div>
  );
}
